import { useEffect, useState, type FormEvent } from 'react';
import type { MonthlyUpdate } from '../types';
import { GlobalButton } from './GlobalButton';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';
import { deleteMonthlyUpdatePdf, uploadMonthlyUpdatePdf } from '../lib/monthlyUpdatePdfStorage';
import { newId } from '../utils/id';

type Props = {
  companyId: string;
  userId: string;
  report?: MonthlyUpdate | null;
  onSubmit: (next: MonthlyUpdate) => void;
  onCancel: () => void;
  onDelete?: () => void;
};

function monthInputFromPeriod(iso?: string): string {
  if (!iso) return '';
  return iso.length >= 7 ? iso.slice(0, 7) : '';
}

function periodFromMonthInput(monthValue: string): string | undefined {
  if (!monthValue || monthValue.length < 7) return undefined;
  return `${monthValue}-01`;
}

export function MonthlyUpdateForm({ companyId, userId, report, onSubmit, onCancel, onDelete }: Props) {
  const [periodMonth, setPeriodMonth] = useState('');
  const [revenue, setRevenue] = useState('');
  const [burn, setBurn] = useState('');
  const [remainingCash, setRemainingCash] = useState('');
  const [reportSentiment, setReportSentiment] = useState('');
  const [exitProbability, setExitProbability] = useState('');
  const [companyHealth, setCompanyHealth] = useState('');
  const [rawText, setRawText] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setPeriodMonth(monthInputFromPeriod(report?.periodMonth));
    setRevenue(report?.revenue != null && Number.isFinite(report.revenue) ? String(report.revenue) : '');
    setBurn(report?.burn != null && Number.isFinite(report.burn) ? String(report.burn) : '');
    setRemainingCash(
      report?.remainingCash != null && Number.isFinite(report.remainingCash)
        ? String(report.remainingCash)
        : ''
    );
    setReportSentiment(
      report?.reportSentiment != null && Number.isFinite(report.reportSentiment)
        ? String(report.reportSentiment)
        : ''
    );
    setExitProbability(
      report?.exitProbability != null && Number.isFinite(report.exitProbability)
        ? String(report.exitProbability)
        : ''
    );
    setCompanyHealth(
      report?.companyHealth != null && Number.isFinite(report.companyHealth)
        ? String(report.companyHealth)
        : ''
    );
    setRawText(report?.rawText ?? '');
    setPdfFile(null);
    setRemovePdf(false);
    setFormError(null);
  }, [report]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const id = report?.id?.trim() ? report.id : newId();

      const numOpt = (s: string): number | undefined => {
        const t = s.trim();
        if (!t) return undefined;
        const n = Number(t);
        return Number.isFinite(n) ? n : undefined;
      };

      const num1to6 = (s: string): number | undefined => {
        const n = numOpt(s);
        if (n === undefined) return undefined;
        return Math.min(6, Math.max(1, Math.round(n)));
      };

      if (removePdf && report?.pdfStoragePath) {
        await deleteMonthlyUpdatePdf(report.pdfStoragePath);
      }

      let pdfUrl: string | undefined = removePdf ? undefined : report?.pdfUrl;
      let pdfStoragePath: string | undefined = removePdf ? undefined : report?.pdfStoragePath;
      let originalPdfFileName: string | undefined = removePdf ? undefined : report?.originalPdfFileName;

      if (pdfFile) {
        if (report?.pdfStoragePath && !removePdf) {
          await deleteMonthlyUpdatePdf(report.pdfStoragePath);
        }
        const uploaded = await uploadMonthlyUpdatePdf(userId, companyId, id, pdfFile);
        pdfUrl = uploaded.pdfUrl;
        pdfStoragePath = uploaded.pdfStoragePath;
        originalPdfFileName = pdfFile.name;
      }

      const periodStored = periodFromMonthInput(periodMonth);
      const revenueN = numOpt(revenue);
      const burnN = numOpt(burn);
      const remainingCashN = numOpt(remainingCash);
      const sentimentN = num1to6(reportSentiment);
      const exitN = num1to6(exitProbability);
      const healthN = num1to6(companyHealth);
      const rt = rawText.trim() ? rawText : undefined;

      onSubmit({
        id,
        companyId,
        periodMonth: periodStored,
        revenue: revenueN,
        burn: burnN,
        remainingCash: remainingCashN,
        reportSentiment: sentimentN,
        exitProbability: exitN,
        companyHealth: healthN,
        rawText: rt,
        pdfUrl,
        pdfStoragePath,
        originalPdfFileName,
        createdAt: report?.createdAt ?? now,
        updatedAt: now,
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const hasExistingPdf = Boolean(report?.pdfUrl && !removePdf);
  const showUndoRemove = Boolean(removePdf && report?.pdfUrl);

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      {formError ? <p className="sidebarFormError">{formError}</p> : null}

      <GlobalInput
        label="Report month (optional)"
        type="month"
        value={periodMonth}
        onChange={(e) => setPeriodMonth(e.target.value)}
      />
      <GlobalInput
        label="Revenue (€ / month, optional)"
        type="number"
        step="any"
        value={revenue}
        onChange={(e) => setRevenue(e.target.value)}
      />
      <GlobalInput
        label="Burn (€ / month, optional)"
        type="number"
        step="any"
        value={burn}
        onChange={(e) => setBurn(e.target.value)}
      />
      <GlobalInput
        label="Remaining cash (€, optional)"
        type="number"
        step="any"
        value={remainingCash}
        onChange={(e) => setRemainingCash(e.target.value)}
      />

      <GlobalInput
        label="Report sentiment (1–6, optional) · 1 😰 → 6 🤩"
        type="number"
        min={1}
        max={6}
        step={1}
        value={reportSentiment}
        onChange={(e) => setReportSentiment(e.target.value)}
      />
      <GlobalInput
        label="Exit probability (1–6, optional) · 1 unlikely → 6 imminent"
        type="number"
        min={1}
        max={6}
        step={1}
        value={exitProbability}
        onChange={(e) => setExitProbability(e.target.value)}
      />
      <GlobalInput
        label="Company health (1–6, optional) · 1 critical → 6 excellent"
        type="number"
        min={1}
        max={6}
        step={1}
        value={companyHealth}
        onChange={(e) => setCompanyHealth(e.target.value)}
      />

      <div>
        <span
          style={{
            fontSize: '0.75rem',
            color: 'var(--secondary-font-color)',
            display: 'block',
            marginBottom: 6,
          }}
        >
          PDF from founder (optional)
        </span>
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => {
            const f = e.target.files?.[0];
            setPdfFile(f ?? null);
            if (f) setRemovePdf(false);
          }}
          style={{ fontSize: 14, width: '100%' }}
        />
        {hasExistingPdf ? (
          <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>
            Current file: {report?.originalPdfFileName || 'report.pdf'}
            {report?.pdfUrl ? (
              <>
                {' '}
                <a href={report.pdfUrl} target="_blank" rel="noopener noreferrer">
                  Open
                </a>
              </>
            ) : null}
          </p>
        ) : null}
        {hasExistingPdf ? (
          <GlobalButton type="button" variant="secondary" onClick={() => setRemovePdf(true)}>
            Remove PDF
          </GlobalButton>
        ) : null}
        {showUndoRemove ? (
          <GlobalButton type="button" variant="secondary" onClick={() => setRemovePdf(false)}>
            Keep PDF
          </GlobalButton>
        ) : null}
        {removePdf && report?.pdfUrl ? (
          <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>
            PDF will be removed when you save.
          </p>
        ) : null}
      </div>

      <GlobalTextarea
        label="Notes / pasted update (optional)"
        value={rawText}
        onChange={(e) => setRawText(e.target.value)}
        rows={14}
        placeholder="Paste long investor updates, KPI commentary, etc."
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', marginTop: 8 }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {onDelete ? (
            <GlobalButton type="button" variant="secondary" destructive onClick={onDelete}>
              Delete
            </GlobalButton>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <GlobalButton type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </GlobalButton>
          <GlobalButton type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </GlobalButton>
        </div>
      </div>
    </form>
  );
}
